'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Crown,
  Check,
  Star,
  Lightning,
  ArrowLeft,
  Sparkle,
  Spinner,
  CreditCard,
} from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import { useRuntimeConfigStore } from '../stores/runtimeConfigStore';
import { subscriptionApi, PlanConfig } from '../services/api';
import { useToastActions } from '../components/ui/Toast';



interface PlanDisplay {
  id: 'basic' | 'free' | 'pro' | 'team' | 'enterprise';
  price: number;
  period: string;
  icon: React.ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone' }>;
  iconColor: string;
  iconBgColor: string;
  features: {
    text: string;
    included: boolean;
  }[];
  popular?: boolean;
}

// 支付提供商类型
type PaymentProvider = 'wechatpay' | 'lemonsqueezy' | 'creem' | 'manual';

// 计划图标映射
const planIcons: Record<string, React.ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone' }>> = {
  free: Star,
  basic: Star,
  pro: Lightning,
  team: Crown,
  enterprise: Crown,
};

// 计划图标颜色映射
const planIconColors: Record<string, { iconColor: string; iconBgColor: string }> = {
  free: { iconColor: 'text-zinc-600', iconBgColor: 'bg-zinc-100' },
  basic: { iconColor: 'text-zinc-600', iconBgColor: 'bg-zinc-100' },
  pro: { iconColor: 'text-amber-600', iconBgColor: 'bg-amber-100' },
  team: { iconColor: 'text-purple-600', iconBgColor: 'bg-purple-100' },
  enterprise: { iconColor: 'text-blue-600', iconBgColor: 'bg-blue-100' },
};

// 与后端 ValidateNoDowngrade 一致：仅允许同级续费或向上升级
const planUpgradeRank: Record<string, number> = {
  free: 0,
  basic: 0,
  pro: 1,
  team: 2,
  ultra: 2,
  enterprise: 3,
};

function isPlanDowngrade(currentPlanId: string, targetPlanId: string): boolean {
  const rc = planUpgradeRank[currentPlanId] ?? -1;
  const rt = planUpgradeRank[targetPlanId] ?? -1;
  if (rc < 0 || rt < 0) return true;
  return rt < rc;
}

/** 已订阅且 status 为 active 时，当前档位 rank ≥ 卡片 rank 则隐藏该卡底部 CTA（含 basic 试用）。legacy plan=free 单独处理。 */
function shouldHideUpgradeNowForPlan(
  subscription: { plan?: string; status?: string } | null | undefined,
  planId: string,
): boolean {
  if (subscription?.status !== 'active') return false;
  const subPlan = (subscription.plan ?? '').toLowerCase();
  if (subPlan === 'free') return false;
  const currentRank = planUpgradeRank[subPlan] ?? -1;
  const cardRank = planUpgradeRank[planId.toLowerCase()] ?? -1;
  if (cardRank < 0) return false;
  return currentRank >= cardRank;
}

export function UpgradePage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { success: toastSuccess, error: toastError } = useToastActions();
  const { subscription, fetchSubscription } = useSubscriptionStore();

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);
  const currentPlan = subscription?.plan ?? 'basic';
  const isBasicTrial = (() => {
    if (subscription?.plan !== 'basic' && subscription?.plan !== 'free') return false;
    if (!subscription?.startedAt || !subscription?.expiresAt) return false;
    const startedAt = new Date(subscription.startedAt).getTime();
    const expiresAt = new Date(subscription.expiresAt).getTime();
    if (Number.isNaN(startedAt) || Number.isNaN(expiresAt)) return false;
    const now = Date.now();
    const trialWindowMs = 8 * 24 * 60 * 60 * 1000; // tolerate timezone/clock drift
    return expiresAt > now && expiresAt-startedAt > 0 && expiresAt-startedAt <= trialWindowMs;
  })();
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'free' | 'pro' | 'team' | 'enterprise'>('pro');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState<PaymentProvider>('manual');
  const [wechatpayEnabled, setWechatpayEnabled] = useState(false);
  const [lemonsqueezyEnabled, setLemonsqueezyEnabled] = useState(false);
  const [creemEnabled, setCreemEnabled] = useState(false);
  
  // 运行时配置
  const { 
    isPaymentProviderSupported,
    isRegionCN,
    isRegionGlobal,
  } = useRuntimeConfigStore();
  const [showTermsConfirm, setShowTermsConfirm] = useState(false);
  
  // 从API获取的计划配置
  const [planConfigs, setPlanConfigs] = useState<PlanConfig[]>([]);
  const [planConfigsLoading, setPlanConfigsLoading] = useState(true);

  // 获取计划配置
  useEffect(() => {
    const fetchPlanConfigs = async () => {
      try {
        const lang = i18n.language || 'zh';
        const res = await subscriptionApi.getPlanConfigs(lang);
        if (res.code === 0 && res.data) {
          setPlanConfigs(res.data);
        }
      } catch (error) {
        console.error('Failed to fetch plan configs:', error);
      } finally {
        setPlanConfigsLoading(false);
      }
    };
    fetchPlanConfigs();
  }, [i18n.language]);

  // 构建显示用的计划列表
  const plans: PlanDisplay[] = planConfigs.map((config) => {
    const icons = planIconColors[config.plan] || planIconColors.free;
    return {
      id: config.plan,
      price: billingCycle === 'yearly' ? config.yearlyPriceUSD : config.monthlyPriceUSD,
      period: billingCycle === 'yearly' ? t('upgrade.perYear') : t('upgrade.perMonth'),
      icon: planIcons[config.plan] || Star,
      iconColor: icons.iconColor,
      iconBgColor: icons.iconBgColor,
      features: config.features.map((feature) => ({ text: feature, included: true })),
      popular: config.popular,
    };
  });

  // 获取计划显示名称
  const getPlanDisplayName = (planId: string) => {
    const config = planConfigs.find((p) => p.plan === planId);
    return config?.name || t(`upgrade.${planId}Plan`);
  };

  const planNames: Record<string, string> = {
    free: getPlanDisplayName('free'),
    basic: getPlanDisplayName('basic'),
    pro: getPlanDisplayName('pro'),
    team: getPlanDisplayName('team'),
    enterprise: getPlanDisplayName('enterprise'),
  };

  // 初始化时设置默认选中的计划：basic/free 用户默认选中 pro（试用期也可以升级到付费计划）
  useEffect(() => {
    if (currentPlan === 'basic' || currentPlan === 'free') {
      setSelectedPlan('pro');
      return;
    }
    setSelectedPlan(currentPlan);
  }, [currentPlan]);

  // 订阅信息异步加载后，避免选中档位低于当前订阅（试用期用户可以保持选中 pro 进行升级）
  useEffect(() => {
    if (isPlanDowngrade(currentPlan, selectedPlan)) {
      if (currentPlan === 'basic' || currentPlan === 'free') {
        // 试用期用户默认选中 pro，允许升级
        setSelectedPlan('pro');
      } else {
        setSelectedPlan(currentPlan);
      }
    }
  }, [currentPlan, selectedPlan]);

  useEffect(() => {
    const loadPaymentOptions = async () => {
      try {
        
        // 国内区域：暂不支持支付（将来支持微信、支付宝）
        if (isRegionCN()) {
          setWechatpayEnabled(false);
          setLemonsqueezyEnabled(false);
          setCreemEnabled(false);
          setPaymentProvider('manual');
          return;
        }

        // 国际区域：检查支持的国际支付方式
        const checks: Promise<{ provider: PaymentProvider; enabled: boolean }>[] = [];
        
        if (isPaymentProviderSupported('creem')) {
          checks.push(
            subscriptionApi.isCreemEnabled()
              .then(res => ({ provider: 'creem' as PaymentProvider, enabled: res.code === 0 && res.data?.enabled }))
              .catch(() => ({ provider: 'creem' as PaymentProvider, enabled: false }))
          );
        }
        
        if (isPaymentProviderSupported('lemonsqueezy')) {
          checks.push(
            subscriptionApi.isLemonSqueezyEnabled()
              .then(res => ({ provider: 'lemonsqueezy' as PaymentProvider, enabled: res.code === 0 && res.data?.enabled }))
              .catch(() => ({ provider: 'lemonsqueezy' as PaymentProvider, enabled: false }))
          );
        }

        const results = await Promise.all(checks);
        
        // 更新状态
        const creemResult = results.find(r => r.provider === 'creem');
        const lemonResult = results.find(r => r.provider === 'lemonsqueezy');
        
        setCreemEnabled(creemResult?.enabled || false);
        setLemonsqueezyEnabled(lemonResult?.enabled || false);
        
        // 国际区域优先级：Creem > LemonSqueezy > 手动
        if (creemResult?.enabled) {
          setPaymentProvider('creem');
        } else if (lemonResult?.enabled) {
          setPaymentProvider('lemonsqueezy');
        } else {
          setPaymentProvider('manual');
        }
      } catch (error) {
        console.error('Failed to load payment options:', error);
        setPaymentProvider('manual');
      }
    };
    loadPaymentOptions();
  }, [isPaymentProviderSupported, isRegionCN]);

  const canSelectPlan = (planId: string) => !isPlanDowngrade(currentPlan, planId);

  const onTermsConfirm = async () => {
    const isSelectedBasic = selectedPlan === 'basic' || selectedPlan === 'free';
    const isCurrentBasic = currentPlan === 'basic' || currentPlan === 'free';
    if (selectedPlan === currentPlan && !(isBasicTrial && isSelectedBasic && isCurrentBasic)) return;
    if (!canSelectPlan(selectedPlan)) {
      toastError(t('upgrade.downgradeNotAllowed'));
      return;
    }
    setShowTermsConfirm(false);
    const plan = selectedPlan;
    const durationMonths = billingCycle === 'yearly' ? 12 : 1;

    const provider =
      paymentProvider === 'creem' && creemEnabled
        ? 'creem'
        : paymentProvider === 'lemonsqueezy' && lemonsqueezyEnabled
          ? 'lemonsqueezy'
          : paymentProvider === 'wechatpay' && wechatpayEnabled
            ? 'zpay'
            : undefined;

    setLoading(true);
    try {
      const res = await subscriptionApi.upgradeSubscription(plan, durationMonths, provider);
      if (res.code === 0 && res.data?.checkoutUrl) {
        window.location.assign(res.data.checkoutUrl);
        return;
      }
      toastError((res as { message?: string })?.message || t('upgrade.createCheckoutFailed'));
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      toastError(msg || t('upgrade.createCheckoutFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    if (!canSelectPlan(selectedPlan)) {
      toastError(t('upgrade.downgradeNotAllowed'));
      return;
    }
    const isSelectedBasic = selectedPlan === 'basic' || selectedPlan === 'free';
    const isCurrentBasic = currentPlan === 'basic' || currentPlan === 'free';
    if (selectedPlan === currentPlan && !(isBasicTrial && isSelectedBasic && isCurrentBasic)) {
      toastSuccess(t('upgrade.alreadySubscribed'));
      return;
    }
    setShowTermsConfirm(true);
  };

  const getPrice = (plan: PlanDisplay | undefined) => {
    if (!plan) return 0;
    const config = planConfigs.find((p) => p.plan === plan.id);
    if (!config) return plan.price;
    
    if (billingCycle === 'yearly') {
      return config.yearlyPriceUSD;
    }
    return config.monthlyPriceUSD;
  };

  const getPlanDescription = (planId: string) => {
    const config = planConfigs.find((p) => p.plan === planId);
    return config?.description || '';
  };

  // 获取支付提供商名称
  const getPaymentProviderName = () => {
    switch (paymentProvider) {
      case 'wechatpay': return t('upgrade.wechatPay');
      case 'lemonsqueezy': return 'LemonSqueezy';
      case 'creem': return 'Creem';
      default: return t('upgrade.manualUpgrade');
    }
  };

  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <div className="max-w-6xl mx-auto p-6">
        {/* 头部 */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">{t('upgrade.title')}</h1>
            <p className="text-sm text-zinc-500">{t('upgrade.subtitle')}</p>
          </div>
        </div>

        {/* 当前计划提示 */}
        {currentPlan && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3"
          >
            <Sparkle className="w-5 h-5 text-emerald-600" weight="fill" />
            <p className="text-sm text-emerald-800">
              {t('upgrade.currentPlanLabel')}: <span className="font-semibold">
                {planNames[currentPlan]}
              </span>
            </p>
          </motion.div>
        )}

        {/* 支付提供商选择 - 仅国际区域显示 */}
        {isRegionGlobal() && (wechatpayEnabled || lemonsqueezyEnabled || creemEnabled) && (
          <div className="mb-6">
            <p className="text-sm text-zinc-600 mb-3">{t('upgrade.selectPaymentMethod')}</p>
            <div className="flex gap-3 flex-wrap">
              {creemEnabled && (
                <button
                  onClick={() => setPaymentProvider('creem')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    paymentProvider === 'creem'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'
                  }`}
                >
                  <CreditCard className="w-5 h-5" />
                  <span className="text-sm font-medium">Creem</span>
                </button>
              )}
              {wechatpayEnabled && (
                <button
                  onClick={() => setPaymentProvider('wechatpay')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    paymentProvider === 'wechatpay'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'
                  }`}
                >
                  <div className="w-5 h-5 rounded bg-[#07c160] flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" weight="bold" />
                  </div>
                  <span className="text-sm font-medium">{t('upgrade.wechatPay')}</span>
                </button>
              )}
              {lemonsqueezyEnabled && (
                <button
                  onClick={() => setPaymentProvider('lemonsqueezy')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    paymentProvider === 'lemonsqueezy'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'
                  }`}
                >
                  <CreditCard className="w-5 h-5" />
                  <span className="text-sm font-medium">LemonSqueezy</span>
                </button>
              )}
              <button
                onClick={() => setPaymentProvider('manual')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  paymentProvider === 'manual'
                    ? 'border-zinc-500 bg-zinc-100 text-zinc-700'
                    : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'
                }`}
              >
                <span className="text-sm font-medium">{t('upgrade.manualUpgrade')}</span>
              </button>
            </div>
          </div>
        )}
        
        {/* 国内区域支付提示 */}
        {isRegionCN() && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm text-amber-800">
              {t('upgrade.paymentComingSoonCN')}
            </p>
          </div>
        )}

        {/* 计费周期切换 */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-xl border border-zinc-200 p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              {t('upgrade.monthlyPay')}
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-zinc-900 text-white'
                  : 'text-zinc-600 hover:text-zinc-900'
              }`}
            >
              {t('upgrade.yearlyPay')}
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                billingCycle === 'yearly' ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700'
              }`}>
                {t('upgrade.savePercent')}
              </span>
            </button>
          </div>
        </div>

        {/* 计划卡片 */}
        {planConfigsLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner className="w-8 h-8 text-emerald-600 animate-spin" />
            <span className="ml-3 text-zinc-600">{t('common.loading')}</span>
          </div>
        ) : (
        <div className="grid grid-cols-3 gap-6 mb-8">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isSelected = selectedPlan === plan.id;
            const isCurrent = currentPlan === plan.id;
            const selectable = canSelectPlan(plan.id);
            const canPayForCurrentBasicTrial =
              isCurrent && isBasicTrial && (plan.id === 'basic' || plan.id === 'free');
            const hideUpgradeNow = shouldHideUpgradeNowForPlan(subscription, plan.id);
            const price = getPrice(plan);

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                onClick={() => {
                  if (!selectable) return;
                  setSelectedPlan(plan.id);
                }}
                className={`relative rounded-2xl border-2 p-6 transition-all ${
                  !selectable
                    ? 'border-zinc-100 bg-zinc-50/80 opacity-60 cursor-not-allowed'
                    : isSelected
                      ? 'border-emerald-500 bg-emerald-50/10 shadow-lg shadow-emerald-500/10 cursor-pointer'
                      : 'border-zinc-200 bg-white hover:border-zinc-300 cursor-pointer'
                }`}
              >
                {/* 推荐标签 */}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-500 text-white text-xs font-medium rounded-full">
                    {t('upgrade.mostPopular')}
                  </div>
                )}

                {/* 当前计划标签 */}
                {isCurrent && (
                  <div className="absolute -top-3 right-4 px-3 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full">
                    {t('upgrade.currentPlanLabel')}
                  </div>
                )}

                {/* 图标 */}
                <div className={`w-12 h-12 rounded-xl ${plan.iconBgColor} flex items-center justify-center mb-4`}>
                  <Icon className={`w-6 h-6 ${plan.iconColor}`} weight="fill" />
                </div>

                {/* 计划名称 */}
                <h3 className="text-lg font-bold text-zinc-900 mb-1">{planNames[plan.id]}</h3>
                <p className="text-xs text-zinc-500 mb-4">{getPlanDescription(plan.id)}</p>

                {/* 价格 */}
                <div className="mb-6">
                  {/* CN区域隐藏pro和enterprise的价格 */}
                  {!(isRegionCN() && (plan.id === 'pro' || plan.id === 'enterprise')) && (
                    <>
                      <span className="text-3xl font-bold text-zinc-900">${price}</span>
                      <span className="text-sm text-zinc-500">{billingCycle === 'yearly' ? t('upgrade.perYear') : plan.period}</span>
                      {billingCycle === 'yearly' && price > 0 && (
                        <p className="text-xs text-zinc-400 mt-1">
                          {t('upgrade.equivalentMonthly', { price: Math.round(price / 12) })}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* 功能列表 */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" weight="bold" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-zinc-300 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-xs ${feature.included ? 'text-zinc-700' : 'text-zinc-400'}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* 已订阅档位及更低档：不渲染底部按钮（顶部已有 Current Plan 角标）；其余档位保留选择与升级 */}
                {!hideUpgradeNow && (
                  <button
                    onClick={(e) => {
                      if (!selectable) return;
                      if (isSelected && (!isCurrent || canPayForCurrentBasicTrial)) {
                        e.stopPropagation();
                        handleUpgrade();
                      }
                    }}
                    className={`w-full py-2.5 text-sm font-medium rounded-xl transition-colors ${
                      !selectable
                        ? 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
                        : isSelected && (!isCurrent || canPayForCurrentBasicTrial)
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700 cursor-pointer'
                          : isCurrent
                            ? 'bg-emerald-100 text-emerald-700 cursor-default'
                            : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200 cursor-pointer'
                    }`}
                  >
                    {!selectable
                      ? t('upgrade.notAvailableLowerTier')
                      : canPayForCurrentBasicTrial
                        ? t('upgrade.upgradeNow')
                        : isCurrent
                          ? t('upgrade.currentPlanLabel')
                          : isSelected
                            ? t('upgrade.selectThisPlan')
                            : t('upgrade.select')}
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
        )}

        {/* 功能对比 */}
        {/* <div className="bg-white rounded-2xl border border-zinc-200/50 p-6 mb-8">
          <h2 className="text-lg font-semibold text-zinc-900 mb-6">{t('upgrade.features')}</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-100">
                  <th className="text-left py-3 px-4 text-sm font-medium text-zinc-600">{t('upgrade.feature')}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-zinc-900">{t('upgrade.freePlan')}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-emerald-600">{t('upgrade.proPlan')}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-purple-600">{t('upgrade.enterprisePlan')}</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-zinc-50">
                  <td className="py-3 px-4 text-zinc-700 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-zinc-400" />
                    {t('upgrade.workflowExecution')}
                  </td>
                  <td className="text-center py-3 px-4 text-zinc-600">{t('upgrade.executions100PerMonth')}</td>
                  <td className="text-center py-3 px-4 text-emerald-600 font-medium">
                    <InfinityIcon className="w-4 h-4 inline" />
                  </td>
                  <td className="text-center py-3 px-4 text-purple-600 font-medium">
                    <InfinityIcon className="w-4 h-4 inline" />
                  </td>
                </tr>
                <tr className="border-b border-zinc-50">
                  <td className="py-3 px-4 text-zinc-700 flex items-center gap-2">
                    <Database className="w-4 h-4 text-zinc-400" />
                    {t('upgrade.dataStorage')}
                  </td>
                  <td className="text-center py-3 px-4 text-zinc-600">1GB</td>
                  <td className="text-center py-3 px-4 text-emerald-600 font-medium">10GB</td>
                  <td className="text-center py-3 px-4 text-purple-600 font-medium">100GB</td>
                </tr>
                <tr className="border-b border-zinc-50">
                  <td className="py-3 px-4 text-zinc-700 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-zinc-400" />
                    {t('upgrade.technicalSupport')}
                  </td>
                  <td className="text-center py-3 px-4 text-zinc-600">{t('upgrade.community')}</td>
                  <td className="text-center py-3 px-4 text-emerald-600 font-medium">{t('upgrade.priority')}</td>
                  <td className="text-center py-3 px-4 text-purple-600 font-medium">{t('upgrade.dedicated')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div> */}

        {/* 底部操作栏：与主内容区对齐，避免 fixed 全宽时被左侧 fixed 侧栏遮挡 */}
        <div
          className="fixed bottom-0 right-0 z-40 bg-white border-t border-zinc-200 p-4"
          style={{ left: 'var(--app-sidebar-width, 0px)' }}
        >
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-zinc-500">{t('upgrade.selected')}</p>
              <p className="text-lg font-bold text-zinc-900 break-words">
                {planNames[selectedPlan]}
                {/* CN区域隐藏pro和enterprise的价格 */}
                {!(isRegionCN() && (selectedPlan === 'pro' || selectedPlan === 'enterprise')) && (
                  <span className="text-sm font-normal text-zinc-500 ml-2 whitespace-nowrap">
                    ${getPrice(plans.find(p => p.id === selectedPlan))}{billingCycle === 'yearly' ? t('upgrade.perYear') : t('upgrade.perMonth')}
                  </span>
                )}
              </p>
              {(wechatpayEnabled || lemonsqueezyEnabled || creemEnabled) && isRegionGlobal() && (
                <p className="text-xs text-zinc-400 mt-1">
                  {t('upgrade.paymentMethod')}: {getPaymentProviderName()}
                </p>
              )}
            </div>
            <button
              onClick={handleUpgrade}
              disabled={(() => {
                if (loading) return true;
                if (!canSelectPlan(selectedPlan)) return true;
                const isSelectedBasic = selectedPlan === 'basic' || selectedPlan === 'free';
                const isCurrentBasic = currentPlan === 'basic' || currentPlan === 'free';
                return selectedPlan === currentPlan && !(isBasicTrial && isSelectedBasic && isCurrentBasic);
              })()}
              className="shrink-0 px-8 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading && <Spinner className="w-4 h-4 animate-spin" />}
              {(() => {
                const isSelectedBasic = selectedPlan === 'basic' || selectedPlan === 'free';
                const isCurrentBasic = currentPlan === 'basic' || currentPlan === 'free';
                return selectedPlan === currentPlan && !(isBasicTrial && isSelectedBasic && isCurrentBasic)
                  ? t('upgrade.currentPlanLabel')
                  : t('upgrade.upgradeNow');
              })()}
            </button>
          </div>
        </div>

        {/* 底部占位 */}
        <div className="h-24" />
      </div>

      {/* 支付条款确认弹窗 */}
      <Dialog.Root open={showTermsConfirm} onOpenChange={(open) => !open && setShowTermsConfirm(false)}>
        <Dialog.Portal>
          <AnimatePresence>
            {showTermsConfirm && (
              <>
                <Dialog.Overlay asChild>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
                  />
                </Dialog.Overlay>
                <Dialog.Content asChild>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl"
                  >
                    <Dialog.Title className="text-lg font-semibold text-zinc-900 mb-4">
                      {t('upgrade.subscriptionConfirm')}
                    </Dialog.Title>
                    <Dialog.Description className="text-sm text-zinc-600 mb-6 leading-relaxed">
                      {t('upgrade.agreeTerms')}
                    </Dialog.Description>
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={() => setShowTermsConfirm(false)}
                        className="px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        onClick={() => onTermsConfirm()}
                        className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                      >
                        {t('upgrade.agreeContinue')}
                      </button>
                    </div>
                  </motion.div>
                </Dialog.Content>
              </>
            )}
          </AnimatePresence>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
